import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BaseButton, Modal, trackJobApplyComplete, useT } from '@repo/common';
import { applyJob } from '../../../api/jobboard/apply.api';

const BottomButton = ({
  view,
  status,
  onClose,
  onDelete,
  onChangeResumeConfirm,
  applyInfo,
  resumeData,
  infoInvalid,
  uploadInvalid,
  uploadData,
  onSubmitAttempt,
  onSuccess,
  jobId,
  job,
  $isMobile,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const { jobId: paramJobId } = useParams();
  const finalJobId = jobId || paramJobId;
  const [showBlockingModal, setShowBlockingModal] = useState(false);

  const uploadedFiles = uploadData.files.map((f) => {
    if (f.isExisting) {
      return {
        isExisting: true,
        id: f.id,
        fileName: f.file.name,
        fileType: f.file.type,
        fileSize: f.file.size,
      };
    }

    return {
      isExisting: false,
      key: f.key,
      fileName: f.file.name,
      fileType: f.file.type,
      fileSize: f.file.size,
    };
  });

  const submitApplication = async () => {
    const resumeInvalid = !resumeData;

    onSubmitAttempt?.({
      infoInvalid,
      resumeInvalid,
    });

    if (infoInvalid || resumeInvalid || !finalJobId) return;

    try {
      const response = await applyJob(finalJobId, {
        ...applyInfo,
        resumeId: resumeData?.id,
        files: uploadedFiles.filter((f) => !f.isExisting),
        links: uploadData.links
          .filter((l) => l.linkUrl && l.linkUrl.trim() !== '')
          .map((l) => ({
            linkType: l.linkType,
            linkUrl: l.linkUrl,
          })),
      });

      trackJobApplyComplete({
        job_id: String(finalJobId),
        job_title: job?.title,
        company: job?.companyName || job?.company,
        application_id: String(response?.jobApplyId),
      });

      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Apply job failed:', error);
    }
  };

  const handleClick = () => {
    if (view === 'changeResume') {
      onChangeResumeConfirm?.();
    }

    if (view !== 'apply') return;

    if (infoInvalid || !resumeData || uploadInvalid) {
      onSubmitAttempt?.({
        infoInvalid,
        resumeInvalid: !resumeData,
        uploadInvalid,
      });
      return;
    }

    if (resumeData?.score < 100 && !infoInvalid) {
      setShowBlockingModal(true);
      return;
    }
    void submitApplication();
  };

  return (
    <div className='w-full shrink-0 flex items-start justify-center p-4 pb-[calc(env(safe-area-inset-bottom)+16px)] tabletH:px-[30px] tabletH:pt-4 tabletH:pb-6 border-t border-grayscale-gray02 bg-grayscale-gray01 tabletH:rounded-b-[30px]'>
      {status === 'view' && (
        <div className='flex flex-col gap-2 tabletH:gap-3 w-full justify-center'>
          <p className='typo-b13 text-text-secondary'>
            {t('Submitted applications cannot be edited. Withdraw, update your resume, and apply again while the job is open.', '제출한 지원서는 수정할 수 없습니다. 지원 취소 후 이력서를 수정하여 모집 중인 공고에 재지원해 주세요.')}
          </p>
          <BaseButton hideIcon type='secondary' size={$isMobile ? 'm' : 'xl'} onClick={onDelete}>
            {t('Withdraw application', '지원 취소')}
          </BaseButton>
          <BaseButton hideIcon size={$isMobile ? 'm' : 'xl'} onClick={onClose}>
            {t('Close', '닫기')}
          </BaseButton>
        </div>
      )}

      {status === 'edit' && (
        <BaseButton
          hideIcon
          size='xl'
          bgclass='w-full tabletH:w-[208px]'
          onClick={handleClick}
        >
          {view === 'changeResume'
            ? t('Change', '변경')
            : t('Submit', '제출')}
        </BaseButton>
      )}

      <Modal
        isOpen={showBlockingModal}
        onClose={() => setShowBlockingModal(false)}
        title={t(
          `Your resume is ${resumeData?.score}% complete`,
          `이력서가 ${resumeData?.score}% 완성되었습니다`,
        )}
        subText={t(
          'A more complete resume can increase your chances of moving to the next stage in Korean hiring processes',
          '이력서를 더 완성하면 한국 채용 과정에서 다음 단계로 진출할 가능성이 높아집니다',
        )}
        leftButtonLabel={t('Complete resume', '이력서 완성하기')}
        rightButtonLabel={t('Continue anyway', '그대로 계속하기')}
        onClickLeftBtn={() => navigate(`/career/resumes/${resumeData?.id}`)}
        onClickRightBtn={() => {
          setShowBlockingModal(false);
          void submitApplication();
        }}
      />
    </div>
  );
};

export default BottomButton;
